import { createRouter, createWebHistory } from "vue-router";
import AuthRoutes from "./auth.ts";
import NonAuthRoutes from "./openPages.js";
import PlatformRoutes from "./platform.ts";
import PlatformHeadlessRoutes from "./platformHeadless.ts";
import PagesRoutes from "./pages.ts";

import startingPages from "@/assets/json/startingPages.json";

import { useUsersStore } from "@/stores/usersStore";
import { useDocumentsStore } from "@/stores/documentsStore.js";

import networkController from "@/js/networkController.js";
import { useBuyersStore } from "@/stores/buyersStore.js";
import { useModalStore } from "@/stores/modalStore.js";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      children: NonAuthRoutes
    },
    {
      path: "/auth/buyer",
      name: "LayoutBuyer",
      component: () => import("@/components/layouts/LayoutBuyer.vue"),
      children: [
        {
          path: "",
          name: "LogInBuyer",
          meta: { name: "Вход для покупателя" },
          component: () => import("@/views/viewLogIn/LogInBuyer.vue"),
        },
        {
          path: "agreement",
          name: "BuyerAgreement",
          meta: { name: "Пользовательское соглашение" },
          component: () => import("@/views/viewBuyer/ViewBuyerAgreement.vue"),
        }
      ]
    },
    {
      path: "/survey/:id",
      component: () => import("@/components/layouts/LayoutBuyer.vue"),
      children: [
        {
          path: "",
          name: "BuyerSurvey",
          meta: { name: "Опрос" },
          component: () => import("@/views/viewSurvey/ViewSurveyBuyer.vue"),
        },
        {
          path: "question/:qid",
          name: "BuyerSurveyQuestion",
          meta: { name: "Вопрос" },
          component: () => import("@/views/viewSurvey/ViewSurveyQuestion.vue"),
        },
        {
          path: "finish",
          name: "BuyerSurveyFinish",
          meta: { name: "Завершение" },
          component: () => import("@/views/viewSurvey/ViewSurveyFinish.vue"),
        }
      ]
    },
    {
      path: "/survey/unavailable",
      component: () => import("@/components/layouts/LayoutBuyer.vue"),
      children: [
        {
          path: "",
          name: "BuyerSurveyUnavailable",
          meta: { name: "Опрос недоступен" },
          component: () => import("@/views/viewSurvey/ViewSurveyUnavailable.vue"),
        }
      ]
    },
    {
      path: "/page/userInstructionDeleteAccount",
      name: "userInstruction",
      component: () => import("@/views/viewUserInstruction/viewUserInstruction.vue"),
      children: PagesRoutes
    },
    {
      path: '/auth',
      name: 'LayoutLogIn',
      component: () => import("@/components/layouts/LayoutLogIn.vue"),
      redirect: {
        name: 'LogInUsersPage'
      },
      children: AuthRoutes
    },
    {
      path: '/',
      name: 'LayoutPlatform',
      component: () => import("@/components/layouts/LayoutDefault.vue"),
      children: PlatformRoutes
    },
    {
      path: '/',
      name: 'LayoutPlatformHeadless',
      component: () => import("@/components/layouts/LayoutDefault.vue"),
      children: PlatformHeadlessRoutes
    },

  ]
})

function checkCookie() {
  let cookieStart = document.cookie.indexOf('access_token=') + 13
  let cookieEnd = document.cookie.indexOf(';', cookieStart) !== -1 ? document.cookie.indexOf(';', cookieStart)
    : document.cookie.length

  return (document.cookie.indexOf('access_token=') !== -1) ? document.cookie?.substring(cookieStart, cookieEnd)  : null
}

function checkBuyerCookie() {
  const key = 'buyer_access_token=';
  let cookieStart = document.cookie.indexOf(key) + key.length;
  if (cookieStart < key.length) return null;
  let cookieEnd = document.cookie.indexOf(';', cookieStart) !== -1 ? document.cookie.indexOf(';', cookieStart) : document.cookie.length;
  return document.cookie.substring(cookieStart, cookieEnd);
}

function checkMeta(to: any, role: string | null) {
  return (to.meta?.access) ? !!(to.meta.access.find((item: string) => item === role)) : true
}

const DEFAULT_TITLE = "KTG"

router.beforeEach(async (to, from, next) => {
  document.title = (to.meta?.name || DEFAULT_TITLE)

  const $userStore = useUsersStore()
  const $documentsStore = useDocumentsStore()
  const $buyersStore = useBuyersStore()
  const $modalStore = useModalStore()

  const path = to.path; // путь без базового префикса

  if(to.fullPath.includes('/welcome')){
    next()
    return
  }
  
  // Покупательская auth — без загрузки профиля
  if (path.startsWith('/auth/buyer')) {
    next();
    return;
  }
  if($userStore?.USER_INFO?.user_role_code === 'owner'){
    $userStore.inviteRequestsList().then(res => {
      if(!$userStore.isOwnerInviteShowed){
        if(res?.data?.length){
          const lastInviteRequest = res?.data?.[res?.data?.length - 1]
          $modalStore.open({ componentName: 'InviteMessages', data: {requests: res?.data, screen: lastInviteRequest.status, from: lastInviteRequest.initiator} })
          $userStore.isOwnerInviteShowed = true
        }
      }
    })
  }

  // Публичные buyer-роуты /survey* — без загрузки профиля
  if (path.startsWith('/survey')) {
    // Разрешить свободный доступ на экран недоступности
    if (path === '/survey/unavailable') {
      next();
      return;
    }
    // Если уже находимся внутри покупательского потока (/survey -> /survey/question, /survey/finish и т.д.) — разрешаем навигацию
    if ((from?.path || '').startsWith('/survey')) {
      next();
      return;
    }
    const knownRole = $userStore?.USER_INFO?.user_role_code || null;
    const surveyId = to.params?.id;
    if (knownRole === 'admin' || knownRole === 'moderator' || knownRole === 'tsm_user' || knownRole === 'owner') {
      next(startingPages[knownRole] || '/403');
      return;
    }
    if (surveyId) {
      try { localStorage.setItem('buyer_survey_id', String(surveyId)); } catch (e) {}
    }
    // Сохраняем tsm_id из query параметров
    const tsmId = to.query?.tsm;
    if (tsmId) {
      try { localStorage.setItem('buyer_tsm_id', String(tsmId)); } catch (e) {}
    }
    const sellerId = (to.query?.seller || to.query?.seller_id);
    if (sellerId) {
      try { localStorage.setItem('buyer_seller_id', String(sellerId)); } catch (e) {}
      // Удаляем seller из URL, чтобы ссылка содержала только id опроса
      const cleanedQuery: any = { ...(to.query || {}) };
      delete cleanedQuery.seller;
      delete cleanedQuery.seller_id;
      next({ path: to.path, query: cleanedQuery, params: to.params });
      return;
    }

    //Запрос на доступность опроса
    if(to.path.startsWith('/survey') && !from.path.startsWith('/auth') && to.params?.id){
      const surveyUrl = new URL(window.location)
      surveyUrl.searchParams.delete('seller')
      try {
        await $buyersStore.checkAvailability(surveyUrl.href).then(res => {
          if(res?.error_code !== 0){
            $buyersStore.setLastErrorMessage('Опрос завершён')
            next('/survey/unavailable?status=completed')
            return
          }else if(res?.error_code === 0){
            $buyersStore.clearLastErrorMessage()
          }
        })
      }catch(e){
        console.error('$buyersStore \n',e)
      }
    }

    // Разрешаем вход на /survey только один раз после успешной авторизации покупателя
    let allowAfterAuth = false;
    try { allowAfterAuth = sessionStorage.getItem('buyer_auth_done') === '1'; } catch (e) {}
    if (allowAfterAuth) {
      try { sessionStorage.removeItem('buyer_auth_done'); } catch (e) {}
      next();
      return;
    }

    // Иначе всегда отправляем на форму покупателя для пере-регистрации
    next('/auth/buyer');
    return;
  }

  // Если это НЕ покупательский поток, чистим buyer_access_token и ведём на стандартную авторизацию
  const buyerCookie = checkBuyerCookie();
  if (buyerCookie) {
    try { document.cookie = 'buyer_access_token=; path=/; max-age=0;'; } catch (e) {}
    if (!path.startsWith('/auth')) {
      next('/auth');
      return;
    }
  }

  // Ниже — существующая логика для платформенных роутов
  if ((path.includes('/auth') || path.includes('/auth/tsm') || path.includes('/auth/admin'))) {
  
  // if ((to.fullPath.includes('/auth') || to.fullPath.includes('/auth/tsm') || to.fullPath.includes('/auth/admin'))) {
    let USER_ROLE = $userStore?.USER_INFO?.user_role_code || null
    const USER_TOKEN = checkCookie()

    // if(to?.query?.seller_code){
    //   console.log('to?.query?.seller_code',to?.query?.seller_code)
    //   try {
    //     localStorage.setItem('seller_code', String(to?.query?.seller_code))
    //   }catch(e) {
    //     console.error('localStorage.setItem seller_code',e)
    //   }
    // }

    if (USER_TOKEN !== null) {
      $userStore.setUserToken(USER_TOKEN, false)
      networkController.defaults.headers.common['Authorization'] = `Bearer ${USER_TOKEN}`
    } else {
      next()
      return
    }

    if (USER_ROLE === null) {

      await $userStore.postFetchUserInfo().then((res) => {

        if (res?.user_role_code) {
          $userStore.USER_INFO = res

          USER_ROLE = $userStore?.USER_INFO?.user_role_code || null
        }
      })
    }

    if (USER_TOKEN !== null) {
      next(startingPages[USER_ROLE])
      return
    }

    next()
    return
  }

  if ((!path.includes('/auth') && !path.includes('/auth/tsm') && !path.includes('/auth/admin'))) {
    const USER_ROLE = $userStore?.USER_INFO?.user_role_code || null
    const USER_TOKEN = checkCookie()

    if (USER_TOKEN === null && !path.includes('/page')) {
      if (path.startsWith('/auth/buyer')) {
        next();
        return;
      }
      if (path.startsWith('/survey')) {
        next();
        return;
      }

      if(to?.query?.seller_code){
        console.log('to?.query?.seller_code',to?.query?.seller_code)
        try {
          localStorage.setItem('seller_code', String(to?.query?.seller_code))
        }catch(e) {
          console.error('localStorage.setItem seller_code',e)
        }
      }

      next('/auth')
      return
    }

    if(USER_ROLE === null && !path.includes('/page')) {
      $userStore.setUserToken(USER_TOKEN, false)
      networkController.defaults.headers.common['Authorization'] = `Bearer ${USER_TOKEN}`

      await $userStore.postFetchUserInfo().then((res) => {

        if (res?.user_role_code) {
          $userStore.USER_INFO = res
        } else {
          next('/auth')
          return
        }
      })

      if($userStore.USER_INFO.user_role_code === 'owner'){
        $userStore.inviteRequestsList().then(res => {
          if(!$userStore.isOwnerInviteShowed){
            if(res?.data?.length){
              const lastInviteRequest = res?.data?.[res?.data?.length - 1]
              $modalStore.open({ componentName: 'InviteMessages', data: {requests: res?.data, screen: lastInviteRequest.status, from: lastInviteRequest.initiator} })
              $userStore.isOwnerInviteShowed = true
            }
          }
        })

        await $documentsStore.postGetDocumentVersion().then((res) => {
          if(!res || !res?.is_signature){
            next('/')
            return
          }
        })
        
        console.log('to.path',to.path)
        console.log('to',to)
        console.log('to?.query?.ref',to?.query?.ref)
        let sellersRef
        if(to.path === '/' && to?.query?.seller_code){
          try {
            localStorage.setItem('seller_code', String(to?.query?.seller_code))
          }catch(e) {
            console.error('localStorage.setItem seller_code',e)
          }
        }
          try {
            sellersRef = localStorage.getItem('seller_code')

            if(sellersRef){
              $userStore.inviteFromSeller({seller_uuid: sellersRef}).then((res) => {
                if(res?.error_code === 0){
                  // $modalStore.open({ componentName: 'InviteMessages', data: {requests: res?.data} })
                  router.replace(router.currentRoute.value.path)
                  $userStore.inviteRequestsList().then(res => {
                    if(res?.data?.length){
                      const lastInviteRequest = res?.data?.[res?.data?.length - 1]
                      $modalStore.open({ componentName: 'InviteMessages', data: {requests: res?.data, screen: lastInviteRequest.status, from: lastInviteRequest.initiator} })
                    }
                  })
                }else {

                }
              }).finally(() => localStorage.removeItem('seller_code'))
            }
          } catch(e) {
            console.error('localStorage.setItem seller_code',e)
          }
      }

      if (checkMeta(to, $userStore.USER_INFO?.user_role_code)) {
        next()
        return
      } else {
        if($userStore.USER_INFO?.user_role_code === 'admin' 
          || $userStore.USER_INFO?.user_role_code === 'moderator' 
          || $userStore.USER_INFO?.user_role_code === 'tsm_user'){
          next(startingPages[$userStore.USER_INFO?.user_role_code])
        }else {
          next('/403')
        }
        return
      }
    }

    if (checkMeta(to, $userStore.USER_INFO?.user_role_code)) {
      next()
      return
    } else {
      if($userStore.USER_INFO?.user_role_code === 'admin' 
        || $userStore.USER_INFO?.user_role_code === 'moderator' 
        || $userStore.USER_INFO?.user_role_code === 'tsm_user'){
        next(startingPages[$userStore.USER_INFO?.user_role_code])
      }else {
        next('/403')
      }
      return
    }
  }

  
  next()
})

// Отслеживание переходов между страницами для Яндекс.Метрики (важно для SPA)
router.afterEach((to) => {
  // Проверяем, что Яндекс.Метрика загружена
  if (typeof window !== 'undefined' && (window as any).ym) {
    (window as any).ym(105889144, 'hit', to.fullPath, {
      title: to.meta?.name || document.title
    });
  }
})

export default router
